/**
 * How much of TODAY is still open, from the scheduler's own free slots.
 *
 * `GET /api/ai-scheduler/free-slots` walks the person's real day — wake,
 * work hours, sleep, work days, buffers, every calendar event and Horton
 * block — and returns the gaps. The band's pill on /today reads it since
 * 2026-09-12 (owner: "use the free-slots endpoint for the free time"),
 * replacing a client formula that assumed 08:00–21:00 and saw only the
 * blocks the page happened to hold.
 *
 * Pure and import-free so the node probe (`npm run test:ori`) can pin it.
 */

export interface FreeSlotLike {
  /** YYYY-MM-DD, the person's local day. */
  date?: string
  /** "HH:MM" */
  start?: string
  /** "HH:MM" */
  end?: string
  duration_minutes?: number
}

function hhmm(v?: string): number | null {
  if (!v) return null
  const m = /^(\d{1,2}):(\d{2})/.exec(v)
  if (!m) return null
  const h = Number(m[1]), mi = Number(m[2])
  if (h > 23 || mi > 59) return null
  return h * 60 + mi
}

/**
 * Minutes still open today, or null when the answer cannot be trusted
 * (nothing for today in the list). The server already starts today's slots
 * at the person's current quarter-hour; the trim to `nowMinutes` is a belt
 * for a slow response, never the source of truth.
 */
export function todayFreeMinutes(
  slots: FreeSlotLike[] | null | undefined,
  todayISO: string,
  nowMinutes: number,
): number | null {
  if (!Array.isArray(slots)) return null
  let total = 0
  let seen = false
  for (const s of slots) {
    if (!s || String(s.date ?? '').slice(0, 10) !== todayISO) continue
    seen = true
    const start = hhmm(s.start)
    const end = hhmm(s.end)
    if (start !== null && end !== null && end > start) {
      const from = Math.max(start, nowMinutes)
      if (end > from) total += end - from
      continue
    }
    const d = Number(s.duration_minutes)
    if (Number.isFinite(d) && d > 0) total += d
  }
  return seen ? Math.max(0, Math.round(total)) : null
}
